import { useState, useEffect } from 'react';
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useFieldArray, useForm, useFormContext } from 'react-hook-form';

import { RHFCardSelect, RHFTextField } from '@/commons/hook-form';
import RHFQuantity from '@/commons/hook-form/RHFQuantity';
import { Iconic } from '@/commons/iconic';
import { Product } from '@/modules/products/types';
import { axios, translates } from '@/utils';
import { getProductsService } from '@/services';
import { yupResolver } from '@hookform/resolvers/yup';
import { orderProductGridSchema } from './validation';

export type AutocompleteLabels = Record<string, any>;

export const ProductItemsGridOrder = ({ isEdit }: { isEdit?: boolean }) => {
  const handleChangeQuantity = () => {
    const { getValues, resetField } = methods;
    const values = getValues();

    setValue(`items[${selected}].quantity`, values.quantity);
    resetField('product');
    resetField('description');
    resetField('quantity');
  };

  const { isLoading, data } = useQuery({
    queryKey: ['products'],
    queryFn: getProductsService,
    select: (dataTemp) => {
      const categories: Record<number, any> = {};
      const products: Record<number, Product[]> = {};
      dataTemp.forEach((product: Product) => {
        const category = product.categories[0];
        if (!categories[category.id]) {
          categories[category.id] = category;
        }
        if (!products[category.id]) {
          products[category.id] = [product];
        } else {
          products[category.id] = [...products[category.id], product];
        }
      });
      return { categories, products };
    },
  });

  const [selected, setSelected] = useState<number>();
  const [showCategoriesMenu, setShowCategoriesMenu] = useState(true);

  const methods = useForm({
    resolver: yupResolver(orderProductGridSchema),
    defaultValues: {
      category: null,
      description: '',
      product: {
        label: '',
        value: '',
      },
      quantity: 1,
    },
  });
  const { control, watch, setValue } = useFormContext();
  const { append, remove } = useFieldArray({
    control,
    name: 'items',
  });

  const handleAdd = () => {
    const { getValues, resetField } = methods;
    const values = getValues();

    append({
      product: values.product,
      description: values.description,
      quantity: values.quantity,
    });
    resetField('product');
    resetField('description');
    resetField('quantity');
  };

  const values = watch();
  const categoryId: any = methods.watch('category');

  useEffect(() => {
    setShowCategoriesMenu(true);
  }, []);

  useEffect(() => {
    if (categoryId) {
      setShowProductsMenu(true);
      setShowCategoriesMenu(false);
      document.getElementById('productoLista')?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [categoryId]);

  const handleSelectItem = (index: number) => {
    if (index === selected) {
      setSelected(undefined);
    } else {
      setSelected(index);
    }
  };

  const handleRemoveItem = (index: number) => {
    remove(index);
  };

  const [showProductsMenu, setShowProductsMenu] = useState(false);

  const handleReopenCategoryMenu = () => {
    setShowCategoriesMenu(true);
    setShowProductsMenu(false);
  };

  if (isLoading) {
    return <CircularProgress size={48} color="info" />;
  }

  return (
    <Grid container spacing={2} sx={{ paddingX: 0 }}>
      <Grid item xs={12} md={8} justifyContent="center">
        {!isEdit ? (
          <Stack
            divider={<Divider flexItem sx={{ borderStyle: 'dashed' }} />}
            spacing={3}
            sx={{
              height: 300,
              overflowY: 'auto',
            }}
          >
            {showProductsMenu ? (
              <Button
                onClick={handleReopenCategoryMenu}
                variant="outlined"
                sx={{ marginTop: 2, width: '100%' }}
              >
                {translates.reopenCategory}
              </Button>
            ): <div />}
            {showCategoriesMenu ? (
              <RHFCardSelect
                name="category"
                wrap
                label={translates.category}
                options={Object.values(data?.categories as any)}
                control={methods.control}
              />
            ) : <div />}
            {showProductsMenu ? (
              <RHFCardSelect
                id="productoLista"
                name="product"
                wrap
                label={translates.products}
                options={Object.values(data?.products[categoryId?.value] as any)}
                control={methods.control}
              />
            ): <div />}
          </Stack>
        ): <div />}
      </Grid>
      <Grid item xs={12} md={4} sx={{ px: 1 }}>
        <Stack spacing={3}>
          <Card sx={{ height: 235 }}>
            <CardContent
              sx={{
                overflowY: 'scroll',
                height: '100%',
              }}
            >
              <Stack spacing={2}>
                {values.items
                  ?.filter((item: any) => item.product !== '' || item)
                  .map((item: any, index: number) => (
                    <Card
                      key={`${item.product.value}-${item.quantity}-${index}`}
                      sx={{ border: `${selected === index && '1px solid violet'}` }}
                      // onClick={() => handleSelectItem(index)}
                    >
                      <CardContent sx={{ display: 'flex ', flexDirection: 'column', gap: '1rem' }}>
                        <Iconic
                          sx={{ alignSelf: 'end' }}
                          icon="material-symbols:cancel-outline"
                          color="red"
                          onClick={() => handleRemoveItem(index)}
                        />

                        <Stack direction="row" justifyContent="space-between" spacing={2}>
                          <Stack justifyContent="center" alignItems="center">
                            <Typography>{item.product.label}</Typography>
                          </Stack>
                          <Stack justifyContent="center" alignItems="center">
                            <Typography fontWeight="bold">
                              {selected ? values.items[selected] : item.quantity}
                            </Typography>
                            <Typography fontSize={12}>{translates.quantity}</Typography>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
              </Stack>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Stack direction="column" spacing={2}>
                <RHFTextField
                  control={methods.control as any}
                  name="description"
                  fullWidth
                  label={translates.description}
                />
                <Stack direction="row" justifyContent="space-between" spacing={2}>
                  <RHFQuantity control={methods.control} name="quantity" />
                  <Stack direction="row" justifyContent="center">
                    <Button
                      size="small"
                      startIcon={<Iconic icon="el:ok-sign" />}
                      disabled={isEdit && selected === undefined}
                      onClick={
                        isEdit
                          ? methods.handleSubmit(handleChangeQuantity)
                          : methods.handleSubmit(handleAdd)
                      }
                      variant="contained"
                      sx={{ flexShrink: 0 }}
                    >
                      {`${translates.add} ${translates.item}`}
                    </Button>
                  </Stack>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Grid>
    </Grid>
  );
};
